import React from "react";
import styled from "styled-components";
// Define local type since ControllerRenderProps is not available in this version
interface ControllerRenderProps {
	onChange: (value: any) => void;
	onBlur: () => void;
	value: any;
	name: string;
}
import { Input, Checkbox, InlineEditor, Select, Textarea } from "components/ui";
import { Slider } from "components";

export const renderInput = (extraProps?: any) => ({
	onChange,
	value,
	onBlur,
	name,
}: ControllerRenderProps) => (
	<Input
		onChange={onChange}
		value={value ?? ""}
		onBlur={onBlur}
		{...extraProps}
		name={name}
	/>
);

export const renderCheckbox = (extraProps?: any) => ({
	onChange,
	onBlur,
	value,
	name,
}: ControllerRenderProps) => (
	<Checkbox
		onBlur={onBlur}
		onChange={(e) => onChange(e.target.checked)}
		defaultChecked={value}
		name={name}
		{...extraProps}
	/>
);

/**
 * components/ui's InlineEditor hardcodes `font-weight: 700` on its input — a
 * literal, inside the compiled bundle, so it cannot be reached from the theme
 * and it never moved when the scale shifted (§2). That left every inline-edited
 * title — the project name in the sidebar most visibly — a full step heavier
 * than the headings beside it. It takes the same weight the global heading rule
 * gives an h1-h6 (AppGlobalStyle.tsx), because that is what it is standing in for.
 *
 * InlineEditor spreads its extra props straight onto the input, so a className
 * from styled() lands where it is needed. `&&` doubles the class so this wins on
 * specificity rather than on injection order. Retire it with the component.
 */
const ScaledInlineEditor = styled(InlineEditor)`
	&& {
		font-weight: var(--font-weight-medium);
	}
`;

export const renderInlineEditor = (extraProps?: any) => ({
	onChange,
	onBlur,
	value,
	name,
}: ControllerRenderProps) => (
	<ScaledInlineEditor
		onChange={onChange}
		value={value ?? ""}
		onBlur={onBlur}
		{...extraProps}
		name={name}
		style={{ border: "none" }}
	/>
);

export const renderSlider = (extraProps?: any) => ({
	onChange,
	value,
	name,
}: {
	onChange: (value: number) => void;
	value: number;
	name: string;
}) => <Slider onChange={onChange} value={value} name={name} {...extraProps} />;

export const renderSelect = (extraProps?: any) => ({
	onChange,
	onBlur,
	value,
	name,
}: ControllerRenderProps) => (
	<Select
		onChange={onChange}
		value={value ?? ""}
		onBlur={onBlur}
		{...extraProps}
		name={name}
	/>
);

export const renderTextarea = (extraProps?: any) => ({
	onChange,
	onBlur,
	value,
	name,
}: ControllerRenderProps) => (
	<Textarea
		onChange={onChange}
		textareaProps={{
			value,
			onBlur,
			name,
			...extraProps?.textareaProps,
		}}
		{...extraProps}
	/>
);
